import * as React from "react";
import { AdminApiController } from "../../../models/AdminApiController.generated";
import { LicenseType } from "../../../models/LicenseType";
import { Profile } from "../../../models/Profile";
import { UserDetails } from "../../../models/UserDetails";
import { SilModal, SilModalProps } from "../../SilModal";
import { CancelSubmitButtons } from "../shared/CancelSubmitButtons";
import { ErrorMessage } from "../shared/ErrorMessage";
import { DropDownField, InputField, ValidatedField } from "../shared/forms";
import { ProfileFormOptions } from "../profiles/ProfileFormOptions";
import { PasswordToggleInput } from "../../PasswordToggleInput";


interface UserModalProps extends SilModalProps {
  user?: UserDetails;
  errorMessage?: string;
  createUser: (userDetails: UserDetails) => void;
  updateUser: (userDetails: UserDetails) => void;
  users: { [userId: number]: UserDetails };
}

interface State {
  wasValidated: boolean;
  userName: ValidatedField<string>;
  name: string;
  designation: string;
  mobile: string;
  email: ValidatedField<string>;
  license: LicenseType;
  password: ValidatedField<string>;
  profileOptions: Profile[];
  profile: string;
  ssoEnabled: boolean;
}

export class UserModal extends React.Component<UserModalProps, State> {
  constructor(props: UserModalProps) {
    super(props);

    if (props.user) {
      // This will be an edit user modal
      this.state = {
        wasValidated: false,
        userName: { value: props.user.userName, isValid: true, message: '' },
        name: props.user.name,
        designation: props.user.designation,
        mobile: props.user.mobile,
        email: { value: props.user.email, isValid: true, message: '' },
        license: props.user.license,
        password: { value: '', isValid: true, message: '' },
        profileOptions: [] as Profile[],
        profile: '',
        ssoEnabled: false
      };
    } else {
      // This will be a new user modal
      this.state = {
        wasValidated: false,
        userName: { value: '', isValid: true, message: '' },
        name: '',
        designation: '',
        mobile: '',
        email: { value: '', isValid: true, message: '' },
        license: LicenseType.ReadWrite,
        password: { value: '', isValid: true, message: '' },
        profileOptions: [] as Profile[],
        profile: '',
        ssoEnabled: false
      }
    }
  }

  componentDidMount() {
    AdminApiController.getProfiles(this.props.user && this.props.user.userID)
      .then(profiles => {
        const { user } = this.props;
        const selectedProfile = user
          ? user.profileId.toString()
          : profiles[0].id.toString();

        this.setState({
          profileOptions: profiles,
          profile: selectedProfile
        });
      });

    AdminApiController.ssoEnabled()
      .then(ssoEnabled => {
        this.setState({
          ssoEnabled: ssoEnabled
        });
      });
  }

  onValidateChange = (e: any) => {
    const state = {
      ...this.state,
      [e.target.name]: {
        ...this.state[e.target.name],
        value: e.target.value
      }
    }

    this.setState(state);
  }

  onNoValidateChange = (e: any) => {
    const state = { ...this.state };
    if (e.target.name === 'email') {
      state['email'].value = e.target.value;
    }
    else {
      state[e.target.name] = e.target.value;
    }

    if (e.target.name == "license" && e.target.value == LicenseType.Read) {
      state.profile = this.state.profileOptions.find(p => p.name == "Read Only").id.toString();
    }

    this.setState(state);
  }

  onUniqueProfileChange = (e: any) => {
    const profiles = [...this.state.profileOptions];
    const oldUnique = profiles.filter(p => p.isUnique === true)[0];
    const newUnique = { ...oldUnique };
    newUnique[e.target.name] = e.target.checked;
    const index = profiles.indexOf(oldUnique);
    profiles[index] = newUnique;

    if (e.target.name == "userTab" && e.target.checked) {
      profiles[index]["viewUsers"] = true;
    }
    if (e.target.name == "areaTab" && e.target.checked) {
      profiles[index]["viewAreas"] = true;
    }

    this.setState({ profileOptions: profiles });
  }

  onSubmit = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.resetValidationStates();
    if (this.formIsValid()) {

      const user = { ...this.props.user } || new UserDetails();
      user.userName = this.state.userName.value;
      user.name = this.state.name;
      user.designation = this.state.designation;
      user.mobile = this.state.mobile;
      user.email = this.state.email.value;
      user.license = this.state.license;
      user.dateFormat = "M/dd/yyy";

      const profile = this.selectedProfile();
      user.profileId = profile.id;
      user.profile = profile;

      if (this.props.user != null) {
        this.props.updateUser(user);
      } else {
        user.password = this.state.password.value;
        this.props.createUser(user);
      }
    }
  }

  formIsValid = () => {
    const { userName, password, email, ssoEnabled } = { ...this.state };

    let users: UserDetails[] = [];
    let keys = Object.keys(this.props.users);
    for (var i = 0; i < keys.length; i++) {
      users.push(this.props.users[keys[i]]);
    }

    let isValid = true;

    // reset state
    userName.isValid = true;
    password.isValid = true;
    email.isValid = true;

    if (userName.value == null || userName.value == '') {
      userName.isValid = false;
      userName.message = "* User name is required";
      isValid = false;
    }
    const approvedChars = userName.value.match(/^[A-Za-z0-9._]*$/);
    const multiplePeriods = (userName.value.match(/\./g) || []).length > 1;
    if (!approvedChars || multiplePeriods) {
      userName.isValid = false;
      userName.message = "User name can only contain letters (A-Z or a-z), numbers (0-9), an underscore (_) and a single period."
      isValid = false;
    }
    if (this.props.user == null) {
      if (password.value == null || password.value == '') {
        password.isValid = false;
        password.message = "* Enter a password";
        isValid = false;
      }
    }
    if (ssoEnabled) {
      if (email.value == '') {
        email.message = "Email can not be an empty.";
        isValid = false;
        email.isValid = false;
      }
      else if (users.filter(u => this.props.user !== u && u.email && u.email.toLowerCase() && u.email == email.value.toLowerCase()).length > 0) {
        email.message = "Email is already in use.";
        isValid = false;
        email.isValid = false;
      }
    }
    else {
      if (email.value != '' && users.filter(u => u.email && this.props.user !== u && u.email.toLowerCase() == email.value.toLowerCase()).length > 0) {
        email.message = "Email is already in use.";
        isValid = false;
        email.isValid = false;
      }
    }
    
    if (!isValid) {
      this.setState({
        userName,
        password,
        email
      })
    }

    this.setState({
      wasValidated: true
    });

    return isValid;
  }

  resetValidationStates = () => {
    const state = JSON.parse(JSON.stringify(this.state));
    Object.keys(state).map(key => {
      if (state[key] && state[key].hasOwnProperty('isValid')) {
        state[key].isValid = true;
        state[key].message = '';
      }
    })
    this.setState(state);
  }

  selectedProfile = () => {
    const profile = this.state.profileOptions
      .filter(p => p.id === parseInt(this.state.profile))[0];

    return profile;
  }

  profilesDisabled = () => {
    return this.state.license == LicenseType.Read;
  }

  render() {
    const { userName } = this.state;

    return (
      <SilModal {...this.props}>
        <form noValidate autoComplete="off">
          <InputField
            label="User name"
            type="text"
            name="userName"
            placeholder="User name"
            value={userName.value}
            onChange={this.onValidateChange}
            invalid={!userName.isValid}
            feedback={userName.message}
            autoFocus={true}
            disabled={this.props.user != null}
            autoComplete="new-user-name"
          />
          <InputField
            label="Name"
            name="name"
            type="text"
            placeholder="Name"
            value={this.state.name}
            onChange={this.onNoValidateChange} />
          <InputField
            name="designation"
            label="Designation"
            type="text"
            placeholder="Enter designation"
            value={this.state.designation}
            onChange={this.onNoValidateChange} />
          <InputField
            label="Mobile"
            name="mobile"
            type="tel"
            placeholder="(123) 456-7890"
            value={this.state.mobile}
            onChange={this.onNoValidateChange} />
          <InputField
            label="Email"
            name="email"
            type="email"
            placeholder="name@company.com"
            value={this.state.email.value}
            invalid={!this.state.email.isValid}
            feedback={this.state.email.message}
            onChange={this.onNoValidateChange} />
          <DropDownField
            label="License"
            name="license"
            value={this.state.license.toString()}
            onChange={this.onNoValidateChange}
          >
            <option value={LicenseType.ReadWrite}>Read and Write</option>
            <option value={LicenseType.Read}>Read Only</option>
            <option value={LicenseType.Inactive}>Inactive</option>
          </DropDownField>
          <div className="form-group row">
            <label className="col-sm-2 col-form-label">Password</label>
            <div className="col-sm-10">
              <React.Fragment>
                <PasswordToggleInput
                  name="password"
                  className={`form-control ${!this.state.password.isValid ? 'is-invalid' : null}`}
                  value={this.state.password.value}
                  onChange={this.onValidateChange}
                  disabled={this.props.user !== null}
                  autoComplete="new-password"
                />
                <div className="invalid-feedback">{this.state.password.message}</div>
              </React.Fragment>
            </div>
          </div>
          <DropDownField
            label="Profile"
            name="profile"
            value={this.state.profile}
            onChange={this.onNoValidateChange}
            disabled={this.profilesDisabled()}>
            {this.state.profileOptions.map((p, i) => {
              return (
                <option
                  key={i}
                  value={p.id}>{p.name}
                </option>
              )
            })}
          </DropDownField>
          {/* this.renderCheckboxes() */}
          {this.selectedProfile() != null
            ? <ProfileFormOptions
              profile={this.selectedProfile()}
              onChange={this.onUniqueProfileChange}
              disabled={!(this.selectedProfile()?.isUnique) ?? true}
            />
            : null
          }
          <ErrorMessage message={this.props.errorMessage} />
          <CancelSubmitButtons
            submitText="Save user"
            closeModal={this.props.closeModal}
            onSubmit={e => this.onSubmit(e)}
          />
        </form>
      </SilModal>
    );
  }
}